import React, { useState, useEffect, useRef, useCallback, useMemo, useId } from "react";
import { Link } from "react-router-dom";
import logo from "../assets/tiply_logo_v5.png";
import mockup from "../assets/tiply_mockup2.png";
import example1 from "../assets/5.png";
import example2 from "../assets/6.png";
import example3 from "../assets/7.png";
import SeoHead from "../components/SeoHead";
import {isSelfServiceSignupEnabled} from "../lib/onboarding";
import {
    DEFAULT_LOCALE,
    LOCALE_CONFIG,
    LOCALE_ORDER,
    LocalizedContent,
    translateText,
} from "../i18n/landingLocales";

/* ═══════════════════════════════════════════════
   HOOKS
   ═══════════════════════════════════════════════ */

function useInView() {
    const ref = useRef(null);
    const [isInView, setIsInView] = useState(() => {
        if (typeof window !== "undefined" && window.__TIPLY_PRERENDER__) return true;
        if (typeof document === "undefined") return false;
        const root = document.getElementById("root");
        return root?.dataset.prerenderRoute === window.location.pathname;
    });
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const observer = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) { setIsInView(true); observer.unobserve(el); } },
            { threshold: 0.15 }
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, []);
    return [ref, isInView];
}

function useCountUp(end, duration = 1800, startCounting = false) {
    const [value, setValue] = useState(() => {
        if (typeof window !== "undefined" && window.__TIPLY_PRERENDER__) return end;
        if (typeof document === "undefined") return 0;
        const root = document.getElementById("root");
        return root?.dataset.prerenderRoute === window.location.pathname ? end : 0;
    });
    useEffect(() => {
        if (!startCounting) return;
        const startTime = performance.now();
        function tick(now) {
            const progress = Math.min((now - startTime) / duration, 1);
            setValue(Math.round((1 - Math.pow(1 - progress, 3)) * end));
            if (progress < 1) requestAnimationFrame(tick);
        }
        requestAnimationFrame(tick);
    }, [startCounting, end, duration]);
    return value;
}

/* ═══════════════════════════════════════════════
   SMALL COMPONENTS
   ═══════════════════════════════════════════════ */

function AnimatedSection({ children, className = "", delay = 0 }) {
    const [ref, inView] = useInView();
    return (
        <div
            ref={ref}
            className={`${className} transition-all duration-700 ${inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
            style={{ transitionDelay: `${delay}ms` }}
        >
            {children}
        </div>
    );
}

function StatItem({ value, suffix, label }) {
    const [ref, inView] = useInView();
    const count = useCountUp(value, 1800, inView);
    return (
        <div ref={ref} className="text-center">
            <div className="text-4xl md:text-5xl font-extrabold text-gray-900 font-bebas tracking-wide">
                {inView ? count : 0}{suffix}
            </div>
            <div className="text-sm text-gray-600 mt-2">{label}</div>
        </div>
    );
}

function FAQItem({ question, answer }) {
    const [open, setOpen] = useState(false);
    const contentRef = useRef(null);
    const [height, setHeight] = useState(0);
    const answerId = useId();

    useEffect(() => {
        if (contentRef.current) setHeight(contentRef.current.scrollHeight);
    }, [answer]);

    return (
        <div className="border-b border-gray-200">
            <button
                onClick={() => setOpen(!open)}
                className="w-full flex items-center justify-between py-5 text-left group"
                aria-expanded={open}
                aria-controls={answerId}
            >
                <span className="text-base font-semibold text-gray-900 group-hover:text-gray-700 transition pr-4">{question}</span>
                <span
                    className={`flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full border-2 border-gray-300 transition-all duration-300 ${open ? "rotate-45 border-greenmint" : ""}`}
                >
                    <svg className="w-3.5 h-3.5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                    </svg>
                </span>
            </button>
            <div
                id={answerId}
                className="overflow-hidden transition-all duration-400 ease-in-out"
                style={{ maxHeight: open ? height : 0 }}
            >
                <div ref={contentRef} className="pb-5 text-sm text-gray-600 leading-relaxed pr-8">
                    {answer}
                </div>
            </div>
        </div>
    );
}

function ContactForm({ locale }) {
    const [result, setResult] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const onSubmit = async (event) => {
        event.preventDefault();
        setSubmitting(true);
        setResult("");

        const formData = new FormData(event.target);
        formData.append("access_key", "f713603e-8bd4-43c5-a6ff-2de9fe5de5d6");
        formData.append("locale", locale);

        try {
            const response = await fetch("https://api.web3forms.com/submit", {
                method: "POST",
                body: formData,
            });
            const data = await response.json();
            if (data.success) {
                setResult("success");
                event.target.reset();
            } else {
                setResult("error");
            }
        } catch {
            setResult("error");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <LocalizedContent locale={locale}>
        <form onSubmit={onSubmit} className="space-y-4 text-left">
            <input type="hidden" name="subject" value="New inquiry from Tiply website" />
            <input type="hidden" name="page_locale" value={locale} />

            <div className="grid sm:grid-cols-2 gap-4">
                <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-gray-700 mb-1.5">Name</label>
                    <input
                        id="contact-name"
                        type="text"
                        name="name"
                        required
                        placeholder="Your name"
                        className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-white text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-greenmint/50 focus:border-greenmint transition"
                    />
                </div>
                <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
                    <input
                        id="contact-email"
                        type="email"
                        name="email"
                        required
                        placeholder="you@company.com"
                        className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-white text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-greenmint/50 focus:border-greenmint transition"
                    />
                </div>
            </div>

            <div>
                <label htmlFor="contact-business" className="block text-sm font-medium text-gray-700 mb-1.5">Business name</label>
                <input
                    id="contact-business"
                    type="text"
                    name="business"
                    placeholder="Your restaurant, café, bar..."
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-white text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-greenmint/50 focus:border-greenmint transition"
                />
            </div>

            <div>
                <label htmlFor="contact-message" className="block text-sm font-medium text-gray-700 mb-1.5">Message <span className="text-gray-400 font-normal">(optional)</span></label>
                <textarea
                    id="contact-message"
                    name="message"
                    rows={3}
                    placeholder="Tell us about your business or ask a question..."
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-white text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-greenmint/50 focus:border-greenmint transition resize-none"
                />
            </div>

            <button
                type="submit"
                disabled={submitting}
                className="w-full bg-black text-white px-6 py-3.5 rounded-lg text-base font-semibold hover:bg-neutral-800 hover:scale-[1.01] transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
            >
                {submitting ? "Sending..." : "Get in Touch"}
            </button>

            {result === "success" && (
                <div role="status" className="flex items-center gap-2 text-sm text-emerald-600 bg-emerald-50 px-4 py-3 rounded-lg">
                    <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Thanks! We'll get back to you within 24 hours.
                </div>
            )}
            {result === "error" && (
                <div role="alert" className="flex items-center gap-2 text-sm text-red-600 bg-red-50 px-4 py-3 rounded-lg">
                    <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    Something went wrong. Please try again or email us at team@tiply.ee.
                </div>
            )}
        </form>
        </LocalizedContent>
    );
}

/* ═══════════════════════════════════════════════
   TAX SAVINGS CALCULATOR
   ═══════════════════════════════════════════════ */

// Estonian 2026 tax rates
const EMPLOYER_MULTIPLIER = 1.338; // gross × 1.338 = total employer cost (33% social + 0.8% unemployment)
const EMPLOYEE_NET_RATE = 0.744;   // gross × 0.744 = employee net (after 22% income + 1.6% unemployment + 2% security)

function TaxCalculator({ locale }) {
    const [sliderValue, setSliderValue] = useState(500);
    const [ref, inView] = useInView();

    // Slider = total amount available for tipping
    // Traditional path: this amount IS the total employer cost (bruto)
    // Work backwards: gross = total / 1.338, then employee net = gross × 0.744
    const totalCost = sliderValue;
    const grossSalary = Math.round(totalCost / EMPLOYER_MULTIPLIER);
    const socialTax = Math.round(grossSalary * 0.33);
    const employerUnemployment = Math.round(grossSalary * 0.008);
    const employeeReceives = Math.round(grossSalary * EMPLOYEE_NET_RATE);
    const totalTaxes = totalCost - employeeReceives;
    const percentLost = totalCost > 0 ? Math.round((1 - employeeReceives / totalCost) * 100) : 0;

    // The fixed €0.25 fee depends on the number of transactions, which this
    // payroll illustration does not ask for. Do not present an exact net payout.
    const tiplyGrossTips = sliderValue;

    // Bar widths (relative to total cost which is the max)
    const barMax = Math.max(totalCost, 1);
    const traditionalBarWidth = (employeeReceives / barMax) * 100;
    const taxBarWidth = (totalTaxes / barMax) * 100;
    const tiplyBarWidth = 100;

    return (
        <LocalizedContent locale={locale}>
        <section id="calculator" className="py-20 md:py-28 bg-white">
            <div className="max-w-5xl mx-auto px-4 md:px-6">
                <AnimatedSection className="text-center mb-12">
                    <h2 className="text-3xl md:text-4xl font-bold">
                        See how much your team is{" "}
                        <span className="bg-gradient-to-r from-red-500 to-orange-400 bg-clip-text text-transparent">losing to taxes</span>
                    </h2>
                    <p className="mt-3 text-gray-500 max-w-2xl mx-auto">
                        Compare an illustrative Estonian payroll route with tips routed directly to staff. Tiply transaction fees and each person's local tax obligations still apply.
                    </p>
                </AnimatedSection>

                <div
                    ref={ref}
                    className={`transition-all duration-700 ${inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
                >
                    {/* Slider */}
                    <div className="bg-gray-50 rounded-2xl p-6 md:p-8 mb-8">
                        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-4">
                            <label className="text-sm font-semibold text-gray-700">Monthly tip amount per employee</label>
                            <div className="text-3xl font-extrabold text-gray-900 font-bebas tracking-wide">
                                €{sliderValue}
                            </div>
                        </div>
                        <input
                            type="range"
                            min={0}
                            max={2000}
                            step={50}
                            value={sliderValue}
                            onChange={(e) => setSliderValue(Number(e.target.value))}
                            className="w-full h-2 bg-gray-200 rounded-full appearance-none cursor-pointer accent-greenmint"
                            style={{
                                background: `linear-gradient(to right, #00ffcc ${(sliderValue / 2000) * 100}%, #e5e7eb ${(sliderValue / 2000) * 100}%)`,
                            }}
                        />
                        <div className="flex justify-between text-xs text-gray-400 mt-2">
                            <span>€0</span>
                            <span>€500</span>
                            <span>€1,000</span>
                            <span>€1,500</span>
                            <span>€2,000</span>
                        </div>
                    </div>

                    {/* Comparison cards */}
                    <div className="grid md:grid-cols-2 gap-6">
                        {/* Traditional */}
                        <div className="bg-white rounded-2xl border border-gray-200 p-6 md:p-8">
                            <div className="flex items-center gap-2 mb-6">
                                <div className="w-3 h-3 rounded-full bg-red-400" />
                                <h3 className="font-bold text-gray-900">Traditional (via payroll)</h3>
                            </div>

                            <div className="space-y-3 mb-6">
                                <div className="bg-gray-50 rounded-lg p-3">
                                    <div className="flex justify-between text-sm mb-1">
                                        <span className="text-gray-500">Total employer cost</span>
                                        <span className="font-bold text-gray-900">€{totalCost}</span>
                                    </div>
                                    <div className="text-xs text-gray-400 space-y-0.5 mt-2">
                                        <div className="flex justify-between">
                                            <span>Gross salary</span>
                                            <span>€{grossSalary}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span>+ Social tax (33%)</span>
                                            <span>€{socialTax}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span>+ Unemployment ins. (0.8%)</span>
                                            <span>€{employerUnemployment}</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-red-50 rounded-lg p-3">
                                    <div className="flex justify-between text-sm mb-1">
                                        <span className="text-gray-700 font-medium">Employee actually receives</span>
                                        <span className="font-bold text-red-500 text-lg">€{employeeReceives}</span>
                                    </div>
                                    <div className="text-xs text-gray-400">
                                        After 22% income + 1.6% unemployment + 2% security tax
                                    </div>
                                </div>
                            </div>

                            {/* Stacked bar */}
                            <div className="h-8 bg-gray-100 rounded-full overflow-hidden flex">
                                <div
                                    className="bg-emerald-400 transition-all duration-500 ease-out rounded-l-full flex items-center justify-center"
                                    style={{ width: `${traditionalBarWidth}%` }}
                                >
                                    {sliderValue > 200 && <span className="text-[10px] font-bold text-white">€{employeeReceives}</span>}
                                </div>
                                <div
                                    className="bg-red-400 transition-all duration-500 ease-out rounded-r-full flex items-center justify-center"
                                    style={{ width: `${taxBarWidth}%` }}
                                >
                                    {sliderValue > 200 && <span className="text-[10px] font-bold text-white">€{totalTaxes} taxes</span>}
                                </div>
                            </div>
                            <div className="text-xs text-red-400 font-medium mt-2 text-center">
                                {sliderValue > 0 ? `${percentLost}% ${translateText(locale, "lost to taxes")}` : "—"}
                            </div>
                        </div>

                        {/* Tiply */}
                        <div className="bg-white rounded-2xl border-2 border-greenmint p-6 md:p-8 relative">
                            <div className="absolute -top-3 right-6 bg-greenmint text-xs font-bold px-3 py-1 rounded-full text-gray-900">
                                WITH TIPLY
                            </div>
                            <div className="flex items-center gap-2 mb-6">
                                <div className="w-3 h-3 rounded-full bg-greenmint" />
                                <h3 className="font-bold text-gray-900">Direct tip route</h3>
                            </div>

                            <div className="space-y-3 mb-6">
                                <div className="bg-gray-50 rounded-lg p-3">
                                    <div className="flex justify-between text-sm mb-1">
                                        <span className="text-gray-500">Business cost</span>
                                        <span className="font-bold text-greenmint">€0</span>
                                    </div>
                                    <div className="text-xs text-gray-400">
                                        Not processed as business payroll in this illustration; local rules may differ
                                    </div>
                                </div>
                                <div className="bg-emerald-50 rounded-lg p-3">
                                    <div className="flex justify-between text-sm mb-1">
                                        <span className="text-gray-700 font-medium">Gross tips before fees</span>
                                        <span className="font-bold text-emerald-600 text-lg">€{tiplyGrossTips}</span>
                                    </div>
                                    <div className="text-xs text-gray-400">
                                        A 7% + €0.25 fee is deducted from each tip; personal taxes may apply
                                    </div>
                                </div>
                            </div>

                            {/* Full green bar */}
                            <div className="h-8 bg-gray-100 rounded-full overflow-hidden">
                                <div
                                    className="h-full bg-gradient-to-r from-emerald-400 to-greenmint transition-all duration-500 ease-out rounded-full flex items-center justify-center"
                                    style={{ width: `${tiplyBarWidth}%` }}
                                >
                                    {sliderValue > 200 && <span className="text-[10px] font-bold text-white">€{tiplyGrossTips} gross tips</span>}
                                </div>
                            </div>
                            <div className="text-xs text-emerald-600 font-medium mt-2 text-center">
                                {sliderValue > 0 ? "Before Tiply fees and personal taxes" : "—"}
                            </div>
                        </div>
                    </div>

                    {/* Summary */}
                    {sliderValue > 0 && (
                        <div className="mt-8 bg-gradient-to-r from-greenmint/10 via-mint/15 to-greenmint/10 rounded-2xl p-6 md:p-8 text-center">
                            <div className="grid sm:grid-cols-2 gap-6">
                                <div>
                                    <div className="text-sm text-gray-500 mb-1">Through payroll, employee gets</div>
                                    <div className="text-3xl md:text-4xl font-extrabold text-red-400 font-bebas tracking-wide">
                                        €{employeeReceives}<span className="text-lg text-gray-500"> of €{totalCost}</span>
                                    </div>
                                    <div className="text-xs text-gray-400">€{totalTaxes} lost to taxes ({percentLost}%)</div>
                                </div>
                                <div>
                                    <div className="text-sm text-gray-500 mb-1">With Tiply</div>
                                    <div className="text-3xl md:text-4xl font-extrabold text-emerald-600 font-bebas tracking-wide">
                                        €{tiplyGrossTips}<span className="text-lg text-gray-500"> gross tips</span>
                                    </div>
                                    <div className="text-xs text-gray-400">Net payout depends on the number of tips, Tiply fees, and personal taxes</div>
                                </div>
                            </div>
                            <button
                                onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}
                                className="mt-6 bg-black text-white px-8 py-3 rounded-lg text-sm font-semibold hover:bg-neutral-800 hover:scale-[1.02] transition-all duration-200"
                            >
                                Start saving — Get in touch
                            </button>
                        </div>
                    )}

                    <p className="text-xs text-gray-400 text-center mt-6">
                        * Estonia-only 2026 payroll illustration, not tax advice. The payroll side uses Estonian rates (33% social tax, 0.8% employer unemployment insurance, 22% income tax, 1.6% employee unemployment, 2% security tax). Tiply deducts 7% + €0.25 from each tip. Local tax treatment varies.
                    </p>
                </div>
            </div>
        </section>
        </LocalizedContent>
    );
}

/* ═══════════════════════════════════════════════
   INDUSTRY ICONS (inline SVG)
   ═══════════════════════════════════════════════ */

const INDUSTRIES = [
    {
        name: "Restaurants",
        icon: (
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 8.25v-1.5m0 1.5c-1.355 0-2.697.056-4.024.166C6.845 8.51 6 9.473 6 10.608v2.513m6-4.87c1.355 0 2.697.055 4.024.165C17.155 8.51 18 9.473 18 10.608v2.513M15 8.25v-1.5m-6 1.5v-1.5m12 9.75l-1.5.75a3.354 3.354 0 01-3 0 3.354 3.354 0 00-3 0 3.354 3.354 0 01-3 0 3.354 3.354 0 00-3 0 3.354 3.354 0 01-3 0L3 16.5m15-3.38a48.474 48.474 0 00-6-.37c-2.032 0-4.034.125-6 .37" />
            </svg>
        ),
    },
    {
        name: "Cafés",
        icon: (
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.362 5.214A8.252 8.252 0 0112 21 8.25 8.25 0 016.038 7.047 8.287 8.287 0 009 9.601a8.983 8.983 0 013.361-6.867 8.21 8.21 0 003 2.48z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 18a3.75 3.75 0 00.495-7.468 5.99 5.99 0 00-1.925 3.547 5.975 5.975 0 01-2.133-1.001A3.75 3.75 0 0012 18z" />
            </svg>
        ),
    },
    {
        name: "Bars",
        icon: (
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714a2.25 2.25 0 00.659 1.591L19 14.5M14.25 3.104c.251.023.501.05.75.082M5 14.5l3.5 6.5h7L19 14.5M5 14.5h14" />
            </svg>
        ),
    },
    {
        name: "Hair Salons",
        icon: (
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M7.848 8.25l1.536.887M7.848 8.25a3 3 0 11-5.196-3 3 3 0 015.196 3zm1.536.887a2.165 2.165 0 011.083 1.839c.005.351.054.695.14 1.024M9.384 9.137l2.077 1.199M7.848 15.75l1.536-.887m-1.536.887a3 3 0 11-5.196 3 3 3 0 015.196-3zm1.536-.887a2.165 2.165 0 001.083-1.838c.005-.352.054-.695.14-1.025m-1.223 2.863l2.077-1.199m0-3.328a4.323 4.323 0 012.068-1.379l5.325-1.628a4.5 4.5 0 012.48-.044l.803.215-7.794 4.5m-2.882-1.664A4.331 4.331 0 0010.607 12m3.736 0l7.794 4.5-.802.215a4.5 4.5 0 01-2.48-.043l-5.326-1.629a4.324 4.324 0 01-2.068-1.379M14.343 12l-2.882 1.664" />
            </svg>
        ),
    },
    {
        name: "Hotels",
        icon: (
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3H21m-3.75 3H21" />
            </svg>
        ),
    },
    {
        name: "Spas",
        icon: (
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0 2.485-2.099 4.5-4.688 4.5-1.935 0-3.597-1.126-4.312-2.733-.715 1.607-2.377 2.733-4.313 2.733C5.1 12.75 3 10.735 3 8.25m9-3.75C9.002 4.5 6 7.117 6 10.5a6.75 6.75 0 006 6.709m0-12.709c2.998 0 6 2.617 6 6a6.75 6.75 0 01-6 6.709m0 0v3.041" />
            </svg>
        ),
    },
];

const FAQS = [
    {
        question: "How does Tiply work?",
        answer: "Each employee (or table/station) gets a unique QR code. Customers scan it with their phone camera, choose a tip amount, and pay instantly via Apple Pay, Google Pay, or card. No app download needed.",
    },
    {
        question: "What payment methods are supported?",
        answer: "We support Apple Pay, Google Pay, and all major credit and debit cards. Payments are processed securely by Stripe.",
    },
    {
        question: "How do employees receive their tips?",
        answer: "Tips are routed to each employee's bank account after the 7% + €0.25 per-tip fee is deducted. Local tax and reporting obligations may apply to the business and recipient.",
    },
    {
        question: "Is there a contract or commitment?",
        answer: "No. Tiply has no contracts, setup fees, monthly fees, or lock-in period. A 7% + €0.25 fee is deducted from every tip transaction.",
    },
    {
        question: "How is this different from POS tipping?",
        answer: "Tiply gives staff a dedicated digital tip route instead of adding tips to the customer's main bill. It also supports team tip pools, feedback, and Google or TripAdvisor review prompts. Payroll and tax treatment depends on local law, so businesses should obtain local advice.",
    },
    {
        question: "Is it secure?",
        answer: "Payments are processed by Stripe, a PCI DSS Level 1 certified payment provider, and Tiply does not store card details. Tiply processes personal data under its Privacy Policy and applicable GDPR obligations.",
    },
    {
        question: "How long does setup take?",
        answer: "Once you share your business and employee details with us, setup is typically completed within a day. More complex or custom setups may take a few days, and onboarding remains subject to required verification.",
    },
    {
        question: "How does team tip splitting work?",
        answer: "Tips can be pooled daily or weekly and split based on logged hours. For example, if a team earns €600 and one person worked 40 hours while another worked 20, the first is allocated €400 and the second €200 before applicable fees and taxes. Optional role weights can adjust each share.",
    },
    {
        question: "Can we use both individual and team tipping?",
        answer: "Yes. Each employee can have a QR code for individual tips while a shared team QR sends tips to the pool. Pool tips are split using the business's selected distribution rules.",
    },
    {
        question: "What does it cost?",
        answer: "There are no monthly or setup fees. QR tipping, team tip pools, shift management, feedback collection, review prompts, analytics, and unlimited employees are included. Tiply deducts 7% + €0.25 from each tip transaction.",
    },
];

function EnglishOnly({ locale }) {
    if (locale === DEFAULT_LOCALE) return null;
    return <span className="ml-1 text-[10px] font-semibold text-gray-400">(EN)</span>;
}

function LanguageSwitcher({ locale, mobile = false }) {
    const [open, setOpen] = useState(false);
    const current = LOCALE_CONFIG[locale];
    const menuId = `language-menu-${locale}-${mobile ? "mobile" : "desktop"}`;

    return (
        <div
            className={`relative ${mobile ? "w-full" : ""}`}
            onKeyDown={(event) => {
                if (event.key === "Escape") {
                    setOpen(false);
                    event.currentTarget.querySelector("button")?.focus();
                }
            }}
        >
            <button
                type="button"
                onClick={() => setOpen((value) => !value)}
                className={`${mobile ? "w-full justify-between" : ""} flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:border-gray-300`}
                aria-expanded={open}
                aria-controls={menuId}
                aria-haspopup="menu"
                aria-label={translateText(locale, "Choose language")}
            >
                <span>{current.shortLabel}</span>
                <span aria-hidden="true">⌄</span>
            </button>
                <div
                    id={menuId}
                    role="menu"
                    aria-hidden={!open}
                    className={`${mobile ? "relative mt-2" : "absolute right-0 mt-2"} ${open ? "visible opacity-100" : "invisible pointer-events-none opacity-0"} z-50 min-w-44 overflow-hidden rounded-xl border border-gray-100 bg-white py-1 shadow-lg transition-opacity`}
                >
                    {LOCALE_ORDER.map((code) => {
                        const item = LOCALE_CONFIG[code];
                        return (
                            <Link
                                key={code}
                                to={item.path}
                                hrefLang={item.hreflang}
                                lang={item.hreflang}
                                role="menuitem"
                                tabIndex={open ? 0 : -1}
                                onClick={() => setOpen(false)}
                                className={`block px-4 py-2 text-sm hover:bg-gray-50 ${code === locale ? "font-bold text-emerald-600" : "text-gray-700"}`}
                            >
                                {item.nativeName}
                            </Link>
                        );
                    })}
                </div>
        </div>
    );
}

/* ═══════════════════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════════════════ */

function LandingPageV3({ locale = DEFAULT_LOCALE }) {
    const [scrolled, setScrolled] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const config = LOCALE_CONFIG[locale] || LOCALE_CONFIG[DEFAULT_LOCALE];
    const localizedFaqs = useMemo(() => [config.availabilityFaq, ...FAQS].map((faq) => ({
        question: translateText(locale, faq.question),
        answer: translateText(locale, faq.answer),
    })), [config.availabilityFaq, locale]);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 20);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const scrollTo = useCallback((id) => {
        setMobileMenuOpen(false);
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }, []);
    const startOnboarding = useCallback(() => {
        setMobileMenuOpen(false);
        if (isSelfServiceSignupEnabled()) window.location.assign("/signup");
        else document.getElementById("contact")?.scrollIntoView({behavior: "smooth"});
    }, []);

    return (
        <>
        <SeoHead
            locale={locale}
            title={config.seo.title}
            description={config.seo.description}
            canonicalPath={config.path}
            faqs={localizedFaqs}
        />
        <LocalizedContent locale={locale}>
        <div className="min-h-screen bg-white text-gray-800 font-urbanist overflow-x-hidden">
            {/* ─── NAVBAR ─── */}
            <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? "bg-white/95 backdrop-blur shadow-sm" : "bg-transparent"}`}>
                <div className="max-w-6xl mx-auto px-4 md:px-6 py-4 flex items-center justify-between">
                    <img src={logo} alt="Tiply" className="h-9 w-auto cursor-pointer" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} />

                    <div className="hidden md:flex items-center gap-8">
                        <button onClick={() => scrollTo("features")} className="text-sm font-medium text-gray-700 hover:text-gray-900 transition">Why Tiply</button>
                        <button onClick={() => scrollTo("team-tipping")} className="text-sm font-medium text-gray-700 hover:text-gray-900 transition">Team Tipping</button>
                        <button onClick={() => scrollTo("how-it-works")} className="text-sm font-medium text-gray-700 hover:text-gray-900 transition">How It Works</button>
                        <button onClick={() => scrollTo("calculator")} className="text-sm font-medium text-gray-700 hover:text-gray-900 transition">Calculator</button>
                        <button onClick={() => scrollTo("pricing")} className="text-sm font-medium text-gray-700 hover:text-gray-900 transition">Pricing</button>
                        <Link to="/referral" className="text-sm font-medium text-greenmint hover:text-emerald-600 transition">Refer & Earn<EnglishOnly locale={locale} /></Link>
                    </div>

                    <div className="hidden md:flex items-center gap-3">
                        <LanguageSwitcher locale={locale} />
                        <Link to="/login" className="text-sm font-medium text-gray-700 hover:text-gray-900 px-4 py-2 transition">Log in<EnglishOnly locale={locale} /></Link>
                        <button onClick={startOnboarding} className="bg-black text-white text-sm font-semibold px-5 py-2.5 rounded-lg hover:bg-neutral-800 transition">
                            Get Started
                        </button>
                    </div>

                    <button className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Toggle menu" aria-expanded={mobileMenuOpen} aria-controls="mobile-navigation">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            {mobileMenuOpen
                                ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                            }
                        </svg>
                    </button>
                </div>

                {mobileMenuOpen && (
                    <div id="mobile-navigation" className="md:hidden bg-white border-t shadow-lg animate-fade-in">
                        <div className="px-4 py-4 flex flex-col gap-3">
                            <button onClick={() => scrollTo("features")} className="text-left text-sm font-medium text-gray-700 py-2">Why Tiply</button>
                            <button onClick={() => scrollTo("team-tipping")} className="text-left text-sm font-medium text-gray-700 py-2">Team Tipping</button>
                            <button onClick={() => scrollTo("how-it-works")} className="text-left text-sm font-medium text-gray-700 py-2">How It Works</button>
                            <button onClick={() => scrollTo("calculator")} className="text-left text-sm font-medium text-gray-700 py-2">Calculator</button>
                            <button onClick={() => scrollTo("pricing")} className="text-left text-sm font-medium text-gray-700 py-2">Pricing</button>
                            <Link to="/referral" className="text-left text-sm font-medium text-greenmint py-2" onClick={() => setMobileMenuOpen(false)}>Refer & Earn<EnglishOnly locale={locale} /></Link>
                            <hr className="border-gray-100" />
                            <Link to="/login" className="text-sm font-medium text-gray-700 py-2">Log in<EnglishOnly locale={locale} /></Link>
                            <LanguageSwitcher locale={locale} mobile />
                            <button onClick={startOnboarding} className="bg-black text-white text-sm font-semibold px-5 py-2.5 rounded-lg text-center hover:bg-neutral-800 transition w-full">
                                Get Started
                            </button>
                        </div>
                    </div>
                )}
            </nav>

            {/* ─── HERO ─── */}
            <header className="relative pt-28 pb-20 md:pt-36 md:pb-28 overflow-hidden">
                {/* Animated gradient background */}
                <div
                    className="absolute inset-0 animate-gradient-shift"
                    style={{
                        background: "linear-gradient(135deg, rgba(0,255,204,0.15) 0%, rgba(168,230,207,0.1) 25%, rgba(178,242,232,0.12) 50%, rgba(0,255,204,0.08) 75%, rgba(168,230,207,0.15) 100%)",
                        backgroundSize: "200% 200%",
                    }}
                />
                <div className="absolute top-20 right-0 w-96 h-96 bg-greenmint/10 rounded-full blur-3xl" />
                <div className="absolute bottom-0 left-0 w-72 h-72 bg-mint/15 rounded-full blur-3xl" />

                <div className="relative max-w-6xl mx-auto px-4 md:px-6 flex flex-col-reverse md:flex-row items-center gap-12">
                    <div className="md:w-1/2 text-center md:text-left">
                        <AnimatedSection>
                            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight tracking-tight">
                                Your team deserves{" "}
                                <span className="bg-gradient-to-r from-emerald-500 to-greenmint bg-clip-text text-transparent">
                                    every tip
                                </span>
                            </h1>
                        </AnimatedSection>
                        <AnimatedSection delay={150}>
                            <p className="mt-5 text-lg text-gray-600 max-w-lg">
                                Accept tips via Apple Pay, Google Pay &amp; cards — no app needed. Route tips to individual staff or split them fairly across your team, subject to local tax rules.
                            </p>
                        </AnimatedSection>
                        <AnimatedSection delay={300}>
                            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center md:justify-start">
                                <button
                                    onClick={startOnboarding}
                                    className="bg-black text-white px-6 py-3 rounded-lg text-base font-semibold hover:bg-neutral-800 hover:scale-[1.02] transition-all duration-200 text-center"
                                >
                                    Create Your Account
                                </button>
                                <button
                                    onClick={() => scrollTo("how-it-works")}
                                    className="border-2 border-gray-200 text-gray-700 px-6 py-3 rounded-lg text-base font-semibold hover:border-gray-300 hover:bg-gray-50 transition-all duration-200 text-center"
                                >
                                    See How It Works
                                </button>
                            </div>
                        </AnimatedSection>
                        <AnimatedSection delay={450}>
                            <p className="mt-6 text-xs text-gray-400 flex items-center justify-center md:justify-start gap-2">
                                <span className="inline-block w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                                Trusted by 50+ businesses across Europe
                            </p>
                        </AnimatedSection>
                    </div>

                    <div className="md:w-1/2 flex justify-center">
                        <img src={mockup} alt="Tiply app mockup" className="w-full max-w-[320px] md:max-w-[400px] drop-shadow-2xl animate-float" />
                    </div>
                </div>
            </header>

            {/* ─── INDUSTRY BAR ─── */}
            <section className="py-10 bg-gray-50 border-y border-gray-100">
                <div className="max-w-5xl mx-auto px-4 md:px-6">
                    <p className="text-center text-xs font-semibold text-gray-400 uppercase tracking-widest mb-6">
                        Built for service businesses
                    </p>
                    <div className="flex flex-wrap justify-center gap-6 md:gap-10">
                        {INDUSTRIES.map((ind, i) => (
                            <div key={i} className="flex flex-col items-center gap-2 text-gray-400 hover:text-emerald-600 transition-colors duration-300">
                                {ind.icon}
                                <span className="text-xs font-medium">{ind.name}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ─── PAIN POINTS ─── */}
            <section className="py-20 md:py-28 bg-white">
                <div className="max-w-5xl mx-auto px-4 md:px-6">
                    <AnimatedSection className="text-center mb-14">
                        <h2 className="text-3xl md:text-4xl font-bold">
                            Cash is disappearing.{" "}
                            <span className="bg-gradient-to-r from-emerald-500 to-greenmint bg-clip-text text-transparent">
                                Tips shouldn't.
                            </span>
                        </h2>
                    </AnimatedSection>

                    <div className="grid md:grid-cols-3 gap-6">
                        {[
                            {
                                icon: (
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z" />
                                    </svg>
                                ),
                                title: "90% of payments are digital",
                                desc: "Most payments in Europe are digital — but tip jars still sit empty. If customers don't carry cash, they simply won't tip.",
                                accent: "bg-amber-50 text-amber-600",
                            },
                            {
                                icon: (
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                ),
                                title: "Payroll tips = heavy taxes",
                                desc: "When tips are processed through payroll, employer and employee deductions can materially reduce the amount staff receive. The exact treatment varies by country.",
                                accent: "bg-red-50 text-red-500",
                            },
                            {
                                icon: (
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v17.25m0 0c-1.472 0-2.882.265-4.185.75M12 20.25c1.472 0 2.882.265 4.185.75M18.75 4.97A48.416 48.416 0 0012 4.5c-2.291 0-4.545.16-6.75.47m13.5 0c1.01.143 2.01.317 3 .52m-3-.52l2.62 10.726c.122.499-.106 1.028-.589 1.202a5.988 5.988 0 01-2.031.352 5.988 5.988 0 01-2.031-.352c-.483-.174-.711-.703-.59-1.202L18.75 4.971zm-16.5.52c.99-.203 1.99-.377 3-.52m0 0l2.62 10.726c.122.499-.106 1.028-.589 1.202a5.989 5.989 0 01-2.031.352 5.989 5.989 0 01-2.031-.352c-.483-.174-.711-.703-.59-1.202L5.25 4.971z" />
                                    </svg>
                                ),
                                title: "Cash tip pools breed conflict",
                                desc: "Splitting cash tips is messy, opaque, and open to disputes. Staff never know if the split was truly fair — and trust erodes fast.",
                                accent: "bg-orange-50 text-orange-500",
                            },
                        ].map((item, i) => (
                            <AnimatedSection key={i} delay={i * 150}>
                                <div className="bg-white p-6 rounded-2xl border border-gray-100 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 h-full">
                                    <div className={`w-12 h-12 rounded-xl ${item.accent} flex items-center justify-center mb-4`}>
                                        {item.icon}
                                    </div>
                                    <h3 className="text-lg font-bold mb-2">{item.title}</h3>
                                    <p className="text-sm text-gray-500 leading-relaxed">{item.desc}</p>
                                </div>
                            </AnimatedSection>
                        ))}
                    </div>

                    <p className="text-xs text-gray-400 text-center mt-6">
                        * The tax example uses Estonian 2026 payroll rules and is not tax advice. Local treatment varies.
                    </p>
                </div>
            </section>

            {/* ─── FEATURES ─── */}
            <section id="features" className="py-20 md:py-28 bg-gray-50">
                <div className="max-w-6xl mx-auto px-4 md:px-6">
                    <AnimatedSection className="text-center mb-14">
                        <h2 className="text-3xl md:text-4xl font-bold">Why businesses choose Tiply</h2>
                        <p className="mt-3 text-gray-500 max-w-2xl mx-auto">
                            Everything you need to modernize tipping, collect feedback, and grow your reputation — in one platform.
                        </p>
                    </AnimatedSection>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[
                            {
                                icon: (
                                    <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                    </svg>
                                ),
                                title: "QR-Based Tipping",
                                desc: "Instant, contactless tipping. Customers scan, choose an amount, and pay in seconds — no app download needed.",
                            },
                            {
                                icon: (
                                    <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                    </svg>
                                ),
                                title: "Feedback Collection",
                                desc: "Gather real-time service feedback from customers after every tip — without any extra tools or surveys.",
                            },
                            {
                                icon: (
                                    <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                                    </svg>
                                ),
                                title: "Google Reviews Boost",
                                desc: "Encourage real reviews from satisfied customers with a single click right after tipping. Grow your reputation automatically.",
                            },
                            {
                                icon: (
                                    <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                    </svg>
                                ),
                                title: "Analytics Dashboard",
                                desc: "Track tips, feedback, and employee performance with a clear, real-time dashboard. Know exactly what's happening.",
                            },
                            {
                                icon: (
                                    <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z" />
                                    </svg>
                                ),
                                title: "Direct Payouts",
                                desc: "Tips are routed to each staff member's bank account — individually or from the team pool — after Tiply fees. Local tax obligations may apply.",
                            },
                            {
                                icon: (
                                    <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
                                    </svg>
                                ),
                                title: "Team Tip Pool",
                                desc: "Pool tips and split them automatically based on hours worked. Weighted by role, transparent to everyone — no more arguments over cash.",
                            },
                            {
                                icon: (
                                    <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5m-9-6h.008v.008H12v-.008zM12 15h.008v.008H12V15zm0 2.25h.008v.008H12v-.008zM9.75 15h.008v.008H9.75V15zm0 2.25h.008v.008H9.75v-.008zM7.5 15h.008v.008H7.5V15zm0 2.25h.008v.008H7.5v-.008zm6.75-4.5h.008v.008h-.008v-.008zm0 2.25h.008v.008h-.008V15zm0 2.25h.008v.008h-.008v-.008zm2.25-4.5h.008v.008H16.5v-.008zm0 2.25h.008v.008H16.5V15z" />
                                    </svg>
                                ),
                                title: "Shift Management",
                                desc: "Log weekly schedules and hours. Tiply uses your roster to calculate each person's fair share of the tip pool automatically.",
                            },
                        ].map((f, i) => (
                            <AnimatedSection key={i} delay={i * 100}>
                                <div className="group bg-white p-6 rounded-2xl border border-gray-100 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 h-full">
                                    <div className="w-12 h-12 rounded-xl bg-greenmint/15 flex items-center justify-center text-emerald-600 mb-4 group-hover:animate-icon-bounce">
                                        {f.icon}
                                    </div>
                                    <h3 className="text-lg font-bold mb-2">{f.title}</h3>
                                    <p className="text-sm text-gray-500 leading-relaxed">{f.desc}</p>
                                </div>
                            </AnimatedSection>
                        ))}
                    </div>
                </div>
            </section>

            {/* ─── HOW IT WORKS ─── */}
            <section id="how-it-works" className="py-20 md:py-28 bg-white">
                <div className="max-w-6xl mx-auto px-4 md:px-6">
                    <AnimatedSection className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold">How it works</h2>
                        <p className="mt-3 text-gray-500 max-w-xl mx-auto">
                            Three simple steps from scan to payout. No downloads, no friction.
                        </p>
                    </AnimatedSection>

                    <div className="grid md:grid-cols-3 gap-8 md:gap-6 relative">
                        <div className="hidden md:block absolute top-24 left-[20%] right-[20%] h-0.5 bg-gradient-to-r from-greenmint/40 via-greenmint to-greenmint/40" />

                        {[
                            { step: 1, title: "Scan the QR Code", desc: "Displayed at the table, counter, or tucked into the bill presenter — customers simply point their camera.", img: example1 },
                            { step: 2, title: "Tip & Review", desc: "Choose a tip amount, leave a star rating, and optionally write feedback.", img: example2 },
                            { step: 3, title: "Get Paid", desc: "Tips are securely processed by Stripe and transferred directly to each staff member.", img: example3 },
                        ].map((s, i) => (
                            <AnimatedSection key={i} delay={i * 200}>
                                <div className="flex flex-col items-center text-center">
                                    <div className="relative z-10 w-12 h-12 rounded-full bg-greenmint flex items-center justify-center text-lg font-bold text-gray-900 shadow-md mb-6">
                                        {s.step}
                                    </div>
                                    <h3 className="text-xl font-bold mb-2">{s.title}</h3>
                                    <p className="text-sm text-gray-500 mb-6 max-w-xs">{s.desc}</p>
                                    <div className="bg-gray-50 rounded-2xl p-4 w-full max-w-[260px]">
                                        <img src={s.img} alt={s.title} className="w-full object-contain rounded-xl" />
                                    </div>
                                </div>
                            </AnimatedSection>
                        ))}
                    </div>
                </div>
            </section>

            {/* ─── HOW TEAM TIPPING WORKS ─── */}
            <section id="team-tipping" className="py-20 md:py-28 bg-gray-50">
                <div className="max-w-6xl mx-auto px-4 md:px-6">
                    <AnimatedSection className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold">
                            Fair tip splitting,{" "}
                            <span className="bg-gradient-to-r from-emerald-500 to-greenmint bg-clip-text text-transparent">
                                on autopilot
                            </span>
                        </h2>
                        <p className="mt-3 text-gray-500 max-w-2xl mx-auto">
                            No more counting cash in the back room. Tiply splits tips transparently based on actual hours worked — a better alternative to manual cash tip pools.
                        </p>
                    </AnimatedSection>

                    {/* 3-step flow */}
                    <div className="grid md:grid-cols-3 gap-8 md:gap-6 relative mb-16">
                        <div className="hidden md:block absolute top-16 left-[20%] right-[20%] h-0.5 bg-gradient-to-r from-greenmint/40 via-greenmint to-greenmint/40" />

                        {[
                            {
                                step: 1,
                                title: "Tips come in",
                                desc: "Customers tip via QR code. Tips accumulate in your team's pool — daily or weekly, your choice.",
                                icon: (
                                    <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 4.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 013.75 9.375v-4.5zM3.75 14.625c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5a1.125 1.125 0 01-1.125-1.125v-4.5zM13.5 4.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 0113.5 9.375v-4.5z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 6.75h.75v.75h-.75v-.75zM6.75 16.5h.75v.75h-.75v-.75zM16.5 6.75h.75v.75h-.75v-.75zM13.5 13.5h.75v.75h-.75v-.75zM13.5 19.5h.75v.75h-.75v-.75zM19.5 13.5h.75v.75h-.75v-.75zM19.5 19.5h.75v.75h-.75v-.75zM16.5 16.5h.75v.75h-.75v-.75z" />
                                    </svg>
                                ),
                            },
                            {
                                step: 2,
                                title: "Hours determine shares",
                                desc: "Each team member's share is calculated from their logged hours. Optional role weights (e.g., servers vs. kitchen staff) ensure fairness.",
                                icon: (
                                    <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                ),
                            },
                            {
                                step: 3,
                                title: "Everyone gets paid",
                                desc: "Tips are distributed directly to bank accounts. Every team member can see exactly how their share was calculated.",
                                icon: (
                                    <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z" />
                                    </svg>
                                ),
                            },
                        ].map((item, i) => (
                            <AnimatedSection key={i} delay={i * 200}>
                                <div className="flex flex-col items-center text-center">
                                    <div className="relative z-10 w-14 h-14 rounded-full bg-greenmint flex items-center justify-center text-gray-900 shadow-md mb-5">
                                        {item.icon}
                                    </div>
                                    <div className="text-xs font-bold text-greenmint mb-2 tracking-wider">STEP {item.step}</div>
                                    <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                                    <p className="text-sm text-gray-500 max-w-xs">{item.desc}</p>
                                </div>
                            </AnimatedSection>
                        ))}
                    </div>

                    {/* Example callout */}
                    <AnimatedSection>
                        <div className="bg-gradient-to-r from-greenmint/10 via-mint/15 to-greenmint/10 rounded-2xl p-6 md:p-8 mb-10">
                            <div className="max-w-3xl mx-auto">
                                <div className="text-sm font-semibold text-emerald-700 mb-3 flex items-center gap-2">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 10-7.517 0c.85.493 1.509 1.333 1.509 2.316V18" />
                                    </svg>
                                    EXAMPLE
                                </div>
                                <p className="text-gray-700 leading-relaxed">
                                    {config.teamExample}
                                </p>
                            </div>
                        </div>
                    </AnimatedSection>

                    {/* Key selling points */}
                    <AnimatedSection delay={200}>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            {[
                                { label: "Transparent", desc: "Every team member sees how their share was calculated", icon: "👁" },
                                { label: "Trustless", desc: "No manual cash counting or \"trust me\" splits", icon: "🤝" },
                                { label: "Fair", desc: "Proportional to actual hours worked, not equal split", icon: "⚖️" },
                                { label: "Flexible", desc: "Optional role weights for different positions", icon: "🎛" },
                            ].map((point, i) => (
                                <div key={i} className="bg-white p-4 rounded-xl border border-gray-100 text-center">
                                    <div className="text-2xl mb-2">{point.icon}</div>
                                    <div className="font-bold text-sm text-gray-900 mb-1">{point.label}</div>
                                    <div className="text-xs text-gray-500 leading-relaxed">{point.desc}</div>
                                </div>
                            ))}
                        </div>
                    </AnimatedSection>
                </div>
            </section>

            {/* ─── TAX CALCULATOR ─── */}
            <TaxCalculator locale={locale} />

            {/* ─── STATS ─── */}
            <section className="py-16 bg-gradient-to-r from-greenmint/10 via-mint/20 to-greenmint/10">
                <div className="max-w-4xl mx-auto px-4 md:px-6 grid grid-cols-2 md:grid-cols-4 gap-8">
                    <StatItem value={90} suffix="%" label="Payments are digital" />
                    <StatItem value={50} suffix="+" label="Businesses on Tiply" />
                    <StatItem value={10} suffix="k+" label="Tips processed" />
                    <StatItem value={5} suffix="★" label="Average rating" />
                </div>
            </section>

            {/* ─── WHAT TEAMS ARE SAYING ─── */}
            <section className="py-20 md:py-28 bg-white">
                <div className="max-w-5xl mx-auto px-4 md:px-6">
                    <AnimatedSection className="text-center mb-14">
                        <h2 className="text-3xl md:text-4xl font-bold">What Tiply means for your team</h2>
                        <p className="mt-3 text-gray-500">Real results from real businesses using Tiply.</p>
                    </AnimatedSection>

                    <div className="grid md:grid-cols-2 gap-6">
                        {[
                            {
                                message: "Staff earn more because customers who pay by card can now tip just as easily as cash customers.",
                                from: "For business owners",
                                accent: "border-l-greenmint",
                                icon: (
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3H21m-3.75 3H21" />
                                    </svg>
                                ),
                            },
                            {
                                message: "No more awkward cash splits at the end of the night. Everyone sees their share — it's fair and automatic.",
                                from: "For team leads & managers",
                                accent: "border-l-emerald-400",
                                icon: (
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
                                    </svg>
                                ),
                            },
                            {
                                message: "My tips are routed to my bank account after Tiply fees, and I can see the amount and history clearly.",
                                from: "For employees",
                                accent: "border-l-teal-400",
                                icon: (
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                                    </svg>
                                ),
                            },
                            {
                                message: "Customers who never used to tip now tip regularly — because tapping their phone is effortless.",
                                from: "The bottom line",
                                accent: "border-l-greenmint",
                                icon: (
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18L9 11.25l4.306 4.307a11.95 11.95 0 015.814-5.519l2.74-1.22m0 0l-5.94-2.28m5.94 2.28l-2.28 5.941" />
                                    </svg>
                                ),
                            },
                        ].map((item, i) => (
                            <AnimatedSection key={i} delay={i * 100}>
                                <div className={`bg-gray-50 rounded-2xl p-6 border-l-4 ${item.accent} hover:shadow-md transition-all duration-300`}>
                                    <p className="text-gray-700 text-sm leading-relaxed mb-4">{item.message}</p>
                                    <div className="flex items-center gap-2 text-xs font-semibold text-gray-500">
                                        <div className="w-6 h-6 rounded-full bg-greenmint/15 flex items-center justify-center text-emerald-600">
                                            {item.icon}
                                        </div>
                                        {item.from}
                                    </div>
                                </div>
                            </AnimatedSection>
                        ))}
                    </div>
                </div>
            </section>

            {/* ─── MISSION ─── */}
            <section className="py-20 md:py-28 bg-gray-50">
                <div className="max-w-6xl mx-auto px-4 md:px-6">
                    <AnimatedSection>
                        <div className="max-w-3xl mx-auto md:mx-0 md:ml-8 border-l-4 border-greenmint pl-8">
                            <h2 className="text-3xl md:text-4xl font-bold mb-4">Our mission</h2>
                            <p className="text-lg text-gray-600 leading-relaxed">
                                At Tiply, we believe tipping should be fast, fair, and digital. We're on a mission to help
                                service workers earn more and help businesses grow through feedback and trust.
                            </p>
                            <p className="text-lg text-gray-600 leading-relaxed mt-4">
                                Whether it's a cozy café, a bustling bar, or a neighborhood hair salon — we're building
                                tools that make appreciation easy and rewarding for everyone.
                            </p>
                        </div>
                    </AnimatedSection>
                </div>
            </section>

            {/* ─── PRICING ─── */}
            <section id="pricing" className="py-20 md:py-28 bg-white">
                <div className="max-w-4xl mx-auto px-4 md:px-6">
                    <AnimatedSection className="text-center mb-14">
                        <h2 className="text-3xl md:text-4xl font-bold">Simple, transparent pricing</h2>
                        <p className="mt-3 text-gray-500">Everything included. No upgrade walls.</p>
                    </AnimatedSection>

                    <div className="max-w-md mx-auto">
                        <AnimatedSection>
                            <div className="bg-white p-8 rounded-2xl border-2 border-greenmint hover:shadow-lg transition-all duration-300 flex flex-col">
                                <div className="flex items-center justify-between mb-6">
                                    <h3 className="text-xl font-bold">Tiply</h3>
                                    <span className="text-4xl font-extrabold">Free</span>
                                </div>
                                <ul className="space-y-3 text-sm text-gray-600 flex-1">
                                    {[
                                        "QR Tipping",
                                        "Team Tip Pool",
                                        "Shift Management",
                                        "Role-Weighted Distribution",
                                        "Feedback Collection",
                                        "Google Review Prompts",
                                        "Advanced Analytics",
                                        "Unlimited Employees",
                                        "Priority Support",
                                    ].map((f, i) => (
                                        <li key={i} className="flex items-center gap-2">
                                            <svg className="w-4 h-4 text-greenmint flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                                            </svg>
                                            {f}
                                        </li>
                                    ))}
                                </ul>
                                <button
                                    onClick={startOnboarding}
                                    className="mt-8 w-full text-center bg-black text-white px-6 py-3 rounded-lg font-semibold hover:bg-neutral-800 hover:scale-[1.02] transition-all duration-200"
                                >
                                    Get Started
                                </button>
                            </div>
                        </AnimatedSection>
                    </div>

                    <AnimatedSection delay={200}>
                        <div className="mt-8 text-center">
                            <div className="inline-block bg-gray-50 rounded-xl px-6 py-4 border border-gray-100">
                                <p className="text-sm text-gray-600">
                                    7% + €0.25 per tip transaction fee. No hidden costs, no monthly fees.
                                </p>
                            </div>
                        </div>
                    </AnimatedSection>
                </div>
            </section>

            {/* ─── FAQ ─── */}
            <section id="faq" className="py-20 md:py-28 bg-gray-50">
                <div className="max-w-3xl mx-auto px-4 md:px-6">
                    <AnimatedSection className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold">Frequently asked questions</h2>
                    </AnimatedSection>

                    <AnimatedSection>
                        <div>
                            {localizedFaqs.map((faq) => (
                                <FAQItem key={faq.question} question={faq.question} answer={faq.answer} />
                            ))}
                        </div>
                    </AnimatedSection>
                </div>
            </section>

            {/* ─── TRUST BADGES ─── */}
            <section className="py-12 bg-white border-y border-gray-100">
                <div className="max-w-4xl mx-auto px-4 md:px-6 flex flex-wrap items-center justify-center gap-8 md:gap-12 text-gray-400">
                    <div className="flex items-center gap-2">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
                        </svg>
                        <span className="text-xs font-semibold">Stripe Secured</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                        </svg>
                        <span className="text-xs font-semibold">PCI Compliant</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" />
                        </svg>
                        <span className="text-xs font-semibold">GDPR Compliant</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3H21m-3.75 3H21" />
                        </svg>
                        <span className="text-xs font-semibold">Estonian Company</span>
                    </div>
                </div>
            </section>

            {/* ─── CONTACT / CTA ─── */}
            <section id="contact" className="relative py-20 md:py-28 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-greenmint/30 via-mint/20 to-teal/20" />
                <div className="absolute top-0 right-0 w-80 h-80 bg-greenmint/15 rounded-full blur-3xl" />
                <div className="absolute bottom-0 left-0 w-64 h-64 bg-mint/20 rounded-full blur-3xl" />

                <div className="relative max-w-2xl mx-auto px-4 md:px-6">
                    <AnimatedSection className="text-center mb-10">
                        <h2 className="text-3xl md:text-4xl font-bold mb-4">
                            Ready to modernize tipping at your business?
                        </h2>
                        <p className="text-lg text-gray-600 max-w-xl mx-auto">
                            Join 50+ businesses already using Tiply. Drop us a message and we'll get you set up.
                        </p>
                    </AnimatedSection>

                    <AnimatedSection delay={200}>
                        <div className="bg-white rounded-2xl p-6 md:p-8 shadow-lg border border-gray-100">
                            <ContactForm locale={locale} />
                        </div>
                        <p className="mt-4 text-sm text-gray-500 text-center">
                            Or email us directly at{" "}
                            <a href="mailto:team@tiply.ee" className="font-medium text-gray-700 underline underline-offset-2">
                                team@tiply.ee
                            </a>
                        </p>
                    </AnimatedSection>
                </div>
            </section>

            {/* ─── FOOTER ─── */}
            <footer className="bg-gray-900 text-gray-400 py-12">
                <div className="max-w-6xl mx-auto px-4 md:px-6">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                        <img src={logo} alt="Tiply" className="h-8 w-auto" />
                        <div className="flex items-center gap-6 text-sm">
                            <Link to="/referral" className="hover:text-white transition">Refer & Earn<EnglishOnly locale={locale} /></Link>
                            <Link to="/supported-countries/" className="hover:text-white transition">Supported countries<EnglishOnly locale={locale} /></Link>
                            <a href="/privacy" className="hover:text-white transition">Privacy<EnglishOnly locale={locale} /></a>
                            <a href="/terms" className="hover:text-white transition">Terms<EnglishOnly locale={locale} /></a>
                            <a href="mailto:team@tiply.ee" className="hover:text-white transition">Contact</a>
                        </div>
                    </div>
                    <div className="mt-8 pt-6 border-t border-gray-800 text-center text-xs text-gray-500">
                        &copy; {new Date().getFullYear()} Tiply. All rights reserved.
                    </div>
                </div>
            </footer>
        </div>
        </LocalizedContent>
        </>
    );
}

export default LandingPageV3;
